/**
 * Alpha Access Service
 *
 * Handles alpha access checks and request submissions for the frontend.
 * Note: API endpoints still use "beta" path for backwards compatibility.
 */

import { getApiUrl } from '../config/api';

const API_BASE_URL = getApiUrl();

export interface CheckEmailResult {
  canRegister: boolean;
  reason: 'whitelisted' | 'existing_user' | 'not_approved';
}

export interface BetaRequestData {
  email: string;
  source: string;
  motivation: string;
  discordHandle?: string;
}

export interface BetaRequestResult {
  success: boolean;
  message: string;
  requestId?: string;
}

/**
 * Check if an email is approved for registration
 */
export async function checkEmailCanRegister(email: string): Promise<CheckEmailResult> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/beta/check-email?email=${encodeURIComponent(email)}`
    );

    if (!response.ok) {
      throw new Error('Failed to check email');
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking email:', error);
    // Fail closed - if we can't verify, don't allow registration
    return { canRegister: false, reason: 'not_approved' };
  }
}

/**
 * Submit a beta access request
 */
export async function submitBetaRequest(data: BetaRequestData): Promise<BetaRequestResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/beta/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: result.error || 'Failed to submit request',
      };
    }

    return {
      success: true,
      message: 'Alpha access request submitted successfully!',
      requestId: result.requestId,
    };
  } catch (error) {
    console.error('Error submitting beta request:', error);
    return {
      success: false,
      message: 'Unable to submit request. Please try again later.',
    };
  }
}
